import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '../Icon';
import { Button } from './Button';
import { cx } from './cx';

// Rediseño · Bloque A. Los tres estados en los que una pantalla todavía no tiene datos que mostrar:
// no hay nada (`EmptyPanel`), algo falló (`ErrorPanel`) o está llegando (`LoadingPanel`). Tienen que
// verse intencionales, no rotos, y decir con palabras qué pasa.
//
// `plain` quita el fondo y la sombra: es para cuando el estado va DENTRO de una tarjeta.

export interface EmptyPanelProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: IconName;
  /** La acción que saca de este estado («Registrar la primera venta»). */
  action?: ReactNode;
  plain?: boolean;
  className?: string;
}

export function EmptyPanel({ title, description, icon, action, plain = false, className }: EmptyPanelProps) {
  return (
    <div className={cx('n3-state', plain && 'n3-state--plain', className)} role="status">
      {icon && <span className="n3-state__icon"><Icon name={icon} size={24} /></span>}
      <p className="n3-state__title">{title}</p>
      {description && <p className="n3-state__description">{description}</p>}
      {action && <div className="n3-state__action">{action}</div>}
    </div>
  );
}

export interface ErrorPanelProps {
  title: ReactNode;
  description?: ReactNode;
  /** Si se pasa, aparece el botón para volver a intentarlo. */
  onRetry?: () => void;
  retryLabel?: string;
  plain?: boolean;
  className?: string;
}

// `role="alert"`: un fallo se anuncia en cuanto aparece, sin esperar a que alguien llegue a él.
export function ErrorPanel({ title, description, onRetry, retryLabel = 'Reintentar', plain = false, className }: ErrorPanelProps) {
  return (
    <div className={cx('n3-state', 'n3-state--error', plain && 'n3-state--plain', className)} role="alert">
      <span className="n3-state__icon"><Icon name="warning" size={26} /></span>
      <p className="n3-state__title">{title}</p>
      {description && <p className="n3-state__description">{description}</p>}
      {onRetry && <div className="n3-state__action"><Button onClick={onRetry} variant="secondary">{retryLabel}</Button></div>}
    </div>
  );
}

export interface SkeletonProps {
  /** Ancho CSS («60%», «120px»). Por defecto ocupa todo el ancho. */
  width?: string;
  /** Alto CSS. Por defecto, una línea de texto. */
  height?: string;
  className?: string;
}

// Marcador gris con brillo. Es decoración: quien anuncia la espera es `LoadingPanel`.
export function Skeleton({ width, height, className }: SkeletonProps) {
  const style: CSSProperties | undefined = width || height ? { width, height } : undefined;
  return <span aria-hidden="true" className={cx('n3-skeleton', className)} style={style} />;
}

export interface LoadingPanelProps {
  /** Lo que se anuncia mientras carga. No se ve: en pantalla están los marcadores. */
  label?: string;
  /** Cuántas líneas dibujar si no se pasan marcadores propios. */
  lines?: number;
  /** Marcadores a la medida (`Skeleton`) cuando la forma del contenido se conoce. */
  children?: ReactNode;
  className?: string;
}

const LINE_WIDTHS = ['100%', '82%', '64%'];

export function LoadingPanel({ label = 'Cargando…', lines = 3, children, className }: LoadingPanelProps) {
  return (
    <div aria-busy="true" className={cx('n3-loading', className)} role="status">
      <span className="n3-visually-hidden">{label}</span>
      {children ?? Array.from({ length: lines }, (_, index) => <Skeleton key={index} width={LINE_WIDTHS[index % LINE_WIDTHS.length]} />)}
    </div>
  );
}
